import React, { useState } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import AchievementSelectorModal from './AchievementSelectorModal';
import './ActionsPanel.scss';

const ActionsPanel = () => {
  const { showAchievementToast } = useToast();
  const [executingAction, setExecutingAction] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  
  // Achievement selector modal state
  const [showAchievementSelector, setShowAchievementSelector] = useState(false);
  const [availableAchievements, setAvailableAchievements] = useState([]);
  const [loadingAchievements, setLoadingAchievements] = useState(false);

  const actions = [
    {
      id: 'refresh-achievements',
      title: 'Refresh Achievements',
      description: 'Recalculate all user achievements based on current requirements.'
    },
    {
      id: 'test-achievement-toast',
      title: 'Test Achievement Unlock',
      description: 'Open achievement selector to test different rarity toast notifications with real achievement data.'
    },
  ];


  const showResultToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  // Fetch achievements for the selector modal
  const fetchAchievements = async () => {
    setLoadingAchievements(true);
    try {
      const response = await apiService.get('/api/achievements');

      if (response.ok) {
        const achievements = await response.json();
        // Filter out locked/placeholder achievements for cleaner testing
        const testableAchievements = achievements.filter(
          achievement => achievement.category !== 'locked' && achievement.category !== 'placeholder'
        );
        setAvailableAchievements(testableAchievements);
      } else {
        console.error('Failed to fetch achievements');
        showResultToast('Failed to load achievements', 'error');
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
      showResultToast('Error loading achievements', 'error');
    } finally {
      setLoadingAchievements(false);
    }
  };

  // Handle achievement selection from modal
  const handleAchievementSelect = (achievement) => {
    setShowAchievementSelector(false);
    
    // Show the achievement toast with the selected achievement
    showAchievementToast(achievement);
    
    // Show success message in admin toast
    showResultToast(`Achievement toast test executed: "${achievement.name}"`, 'success');
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowAchievementSelector(false);
  };

  const executeAction = async (actionId) => {
    const action = actions.find(a => a.id === actionId);
    
    // Handle test achievement toast action separately (no confirmation needed)
    if (actionId === 'test-achievement-toast') {
      setExecutingAction(actionId);
      
      // Fetch achievements and open the selector modal
      await fetchAchievements();
      setShowAchievementSelector(true);
      
      setExecutingAction(null);
      return;
    }

    setExecutingAction(actionId);

    try {
      let response;

      if (actionId === 'refresh-achievements') {
        response = await apiService.post('/api/admin/actions/refresh-achievements');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Action failed');
      }
      
      showResultToast(`${action.title} executed successfully!`, 'success');

    } catch (error) {
      console.error(`Failed to execute: ${action.title}`, error);
      showResultToast(`Error: ${error.message}`, 'error');
    } finally {
      setExecutingAction(null);
    }
  };

  return (
    <div className="actions-panel">
      <div className="admin-page__section">
        <h2>Administrative Actions</h2>
        <div className="actions-table">
          <table className="actions-table__table">
            <thead>
              <tr>
                <th className="actions-table__header">Action</th>
                <th className="actions-table__header">Description</th>
                <th className="actions-table__header actions-table__header--centered">Execute</th>
              </tr>
            </thead>
            <tbody>
              {actions.map(action => (
                <tr key={action.id} className="actions-table__row">
                  <td className="actions-table__cell actions-table__cell--title">
                    {action.title}
                  </td>
                  <td className="actions-table__cell">
                    {action.description}
                  </td>
                  <td className="actions-table__cell actions-table__cell--actions">
                    <button
                      className={`actions-table__button ${executingAction === action.id ? 'actions-table__button--loading' : ''}`}
                      onClick={() => executeAction(action.id)}
                      disabled={!!executingAction}
                    >
                      {executingAction === action.id ? (
                        <span className="spinner"></span>
                      ) : (
                        'Execute'
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showToast && (
        <div className={`toast toast--${toastType}`}>
          <div className="toast__content">
            <span className="toast__icon">{toastType === 'success' ? '✓' : '✗'}</span>
            <span className="toast__message">{toastMessage}</span>
          </div>
        </div>
      )}

      <AchievementSelectorModal
        isOpen={showAchievementSelector}
        onClose={handleModalClose}
        onSelectAchievement={handleAchievementSelect}
        availableAchievements={availableAchievements}
        loading={loadingAchievements}
      />
    </div>
  );
};

export default ActionsPanel;